import { openReviewer } from '@/api/open'
import { mapGetters } from 'vuex'
import config from '@/constant/config'

const TURNSTILE_SCRIPT =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
let turnstileScriptPromise = null

function loadTurnstileScript() {
  if (window.turnstile) {
    return Promise.resolve(window.turnstile)
  }
  // 只加载一次 Turnstile 脚本，避免页面反复进入时重复插入 script。
  if (!turnstileScriptPromise) {
    turnstileScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = TURNSTILE_SCRIPT
      script.async = true
      script.defer = true
      script.onload = () => resolve(window.turnstile)
      script.onerror = () => {
        turnstileScriptPromise = null
        reject(new Error('Turnstile script load failed'))
      }
      document.head.appendChild(script)
    })
  }
  return turnstileScriptPromise
}

export default {
  components: {},
  data() {
    return {
      detail: {},
      code: '',
      turnstileToken: '',
      turnstileWidgetId: null,
      turnstileReady: false,
      turnstileLoading: false,
      turnstileError: '',
      submitLoading: false
    }
  },
  computed: {
    ...mapGetters({
      manuscriptType: 'getManuscriptType'
    })
  },
  mounted() {
    this.code = this.$route.query.reviewskey
    if (this.code) {
      this.getQuery(this.code)
    }
    this.$nextTick(() => {
      this.initTurnstile()
    })
  },
  beforeDestroy() {
    this.removeTurnstile()
  },
  methods: {
    async getQuery() {
      const data = await openReviewer.reviewsCode(this.code)
      this.detail = data
    },
    cancel() {
      this.$router.push('/access/login')
    },
    async initTurnstile() {
      if (!config.siteKey || !this.$refs.turnstile) {
        return
      }
      this.turnstileLoading = false
      this.turnstileError = ''
      try {
        const turnstile = await loadTurnstileScript()
        if (this.turnstileWidgetId !== null || !this.$refs.turnstile) {
          return
        }
        this.turnstileWidgetId = turnstile.render(this.$refs.turnstile, {
          sitekey: config.siteKey,
          action: 'review_confirm',
          appearance: 'interaction-only',
          // 使用 Managed + interaction-only：正常情况下尽量隐藏组件，只在 Cloudflare 判定需要交互时展示挑战。
          // 这不能保证所有真人都无感通过，是否需要点击由 Cloudflare 根据域名、网络、浏览器环境等风险信号决定。
          callback: (token) => {
            this.turnstileToken = token
            this.turnstileLoading = false
            this.turnstileError = ''
          },
          // token 有效期较短，过期后必须清空，不能继续提交旧 token。
          'expired-callback': () => {
            this.turnstileToken = ''
            this.turnstileLoading = false
            this.turnstileError =
              'Verification expired. Please complete it again.'
          },
          // 验证失败时清空 token，让用户在当前页面重新完成 Turnstile。
          'error-callback': () => {
            this.turnstileToken = ''
            this.turnstileLoading = false
            this.turnstileError = 'Verification failed. Please try again.'
          },
          'timeout-callback': () => {
            this.turnstileToken = ''
            this.turnstileLoading = false
            this.turnstileError = 'Verification timed out. Please try again.'
          },
        })
        this.turnstileReady = true
      } catch (e) {
        this.turnstileError = 'Verification component failed to load.'
        this.turnstileReady = false
      } finally {
        this.turnstileLoading = false
      }
    },
    resetTurnstile() {
      this.turnstileToken = ''
      if (window.turnstile && this.turnstileWidgetId !== null) {
        // 后端拒绝或 token 失效后重置组件，重新生成下一次提交要用的 token。
        window.turnstile.reset(this.turnstileWidgetId)
      }
    },
    removeTurnstile() {
      if (window.turnstile && this.turnstileWidgetId !== null) {
        window.turnstile.remove(this.turnstileWidgetId)
      }
    },
    async submit() {
      if (this.submitLoading) {
        return
      }
      if (!this.turnstileToken) {
        // 不禁用接受按钮，避免审稿人误以为不能继续；点击时温和提示先完成人机验证。
        this.turnstileError =
          'Please complete the quick verification above, then accept the invitation.'
        if (this.$refs.turnstile && this.$refs.turnstile.scrollIntoView) {
          this.$refs.turnstile.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
        return
      }
      this.submitLoading = true
      try {
        let data = {
          id: this.detail.id,
          status: 'AGREED',
          version: this.detail.version,
          turnstileToken: this.turnstileToken
        }
        await openReviewer.confirmCode(this.code, data)
        this.$router.push(`/email-url/review-agree?reviewskey=${this.code}`)
      } catch (e) {
        // 后端校验失败或 token 过期时，重置验证组件，允许用户再次点击重试。
        this.resetTurnstile()
        if (!this.turnstileError) {
          this.turnstileError = 'Verification failed. Please try again.'
        }
      } finally {
        this.submitLoading = false
        this.turnstileLoading = false
      }
    }
  }
}
